import React, { useState, useEffect } from "react";
import TopBar from "../../common/TopBar";
import Sidebar from "../../common/Sidebar";

import { DataGrid } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { Box, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import axios from "axios";
import { FiEdit } from "react-icons/fi";
import { fetchTourDetails } from "../../../services/adminRestapiService";
import { getImageUploadError, IMAGE_UPLOAD_ACCEPT, IMAGE_UPLOAD_HELP } from "../../../utils/imageUpload";

const API_URL = "https://api.gosehari.com"



function ViewTourDetails() {
    const { id } = useParams();

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [filterType, setFilterType] = useState("all");

    // EDIT MODAL
    const [openEditModal, setOpenEditModal] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [editImage, setEditImage] = useState(null);

    // ADD MODAL
    const [openAddModal, setOpenAddModal] = useState(false);
    const [newDetail, setNewDetail] = useState({
        detail_type: "",
        day_number: "",
        title: "",
        value: "",
    });
    const [newImage, setNewImage] = useState(null);

    const toggleSidebar = () => setSidebarOpen((isCurrentlyOpen) => !isCurrentlyOpen);

    // ===============================
    // FETCH TOUR DATA
    // ===============================
    const loadData = () => {
        const token = localStorage.getItem("authToken");
        setLoading(true);

        fetchTourDetails(token, id)
            .then((res) => {
                const data = res.data;

                const formattedDetailRows = data.details.map((d) => ({
                    id: d.id,
                    detail_type: d.detail_type,
                    day_number: d.day_number || "-",
                    title: d.title || "-",
                    value: d.value,
                    image: d.detail_type === "image" ? d.value : null,
                }));

                const mainTourRow = {
                    id: data.tour.id,
                    detail_type: "tour",
                    day_number: "-",
                    title: data.tour.title,
                    value: data.tour.subtitle,
                    image: data.tour.header_image,
                };

                setTableData([mainTourRow, ...formattedDetailRows]);
                setLoading(false);
            })
            .catch(() => {
                setError("Failed to load tour details");
                setLoading(false);
            });
    };

    useEffect(() => {
        loadData();
    }, [id]);

    // ===============================
    // OPEN EDIT MODAL
    // ===============================
    const handleEditClick = (row) => {
        setEditRow({ ...row });
        setEditImage(null);
        setOpenEditModal(true);
    };

    // ===============================
    // HANDLE EDIT INPUT CHANGE
    // ===============================
    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditRow((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditFileChange = (e) => {
        const file = e.target.files[0];
        const imageError = getImageUploadError(file);
        if (imageError) {
            toast.error(imageError);
            e.target.value = "";
            return;
        }
        if (file) {
            setEditImage(file);
        }
    };

    const handleNewImageChange = (e) => {
        const file = e.target.files[0];
        const imageError = getImageUploadError(file);
        if (imageError) {
            toast.error(imageError);
            e.target.value = "";
            return;
        }
        setNewImage(file || null);
    };

    // ===============================
    // SUBMIT EDIT UPDATE
    // ===============================
    const handleEditSubmit = async () => {
        const token = localStorage.getItem("authToken");

        try {
            // CASE: Updating MAIN TOUR
            if (editRow.detail_type === "tour") {
                const payload = {
                    title: editRow.title,
                    subtitle: editRow.value,
                };

                await axios.put(`${API_URL}/tour/${editRow.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                toast.success("Main tour updated!");
                setOpenEditModal(false);
                loadData();
                return;
            }

            // CASE: Editing detail
            let payload;
            let headers = {};

            if (editRow.detail_type === "image") {
                payload = new FormData();
                payload.append("detail_type", editRow.detail_type);
                payload.append("title", editRow.title);
                payload.append("value", editRow.value);
                payload.append("day_number", editRow.day_number);

                if (editImage) payload.append("image", editImage);

                headers = {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                };
            } else {
                payload = {
                    detail_type: editRow.detail_type,
                    title: editRow.title,
                    value: editRow.value,
                    day_number: editRow.day_number,
                };

                headers = { Authorization: `Bearer ${token}` };
            }

            await axios.put(`${API_URL}/tour/detail/${editRow.id}`, payload, { headers });

            toast.success("Detail updated successfully!");
            setOpenEditModal(false);
            loadData();
        } catch (err) {
            console.error(err);
            toast.error("Failed to update");
        }
    };

    // ===============================
    // ADD NEW DETAIL SUBMIT
    // ===============================
    const handleAddSubmit = async () => {
        const token = localStorage.getItem("authToken");

        try {
            let payload;
            let headers;

            if (newDetail.detail_type === "image") {
                payload = new FormData();
                payload.append("detail_type", newDetail.detail_type);
                payload.append("day_number", newDetail.day_number);
                payload.append("title", newDetail.title);
                payload.append("value", newDetail.value);
                if (newImage) payload.append("image", newImage);

                headers = {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                };
            } else {
                payload = {
                    detail_type: newDetail.detail_type,
                    day_number: newDetail.day_number,
                    title: newDetail.title,
                    value: newDetail.value,
                };

                headers = { Authorization: `Bearer ${token}` };
            }

            await axios.post(`${API_URL}/tour/detail/${id}`, payload, { headers });

            toast.success("New detail added!");
            setOpenAddModal(false);
            loadData();
        } catch (err) {
            console.error(err);
            toast.error("Failed to add detail");
        }
    };

    // ===============================
    // DATAGRID COLUMNS
    // ===============================
    const columns = [
        { field: "id", headerName: "ID", flex: 0.4 },

        {
            field: "detail_type",
            headerName: "Type",
            flex: 1,
            valueFormatter: (params) => params.value?.toUpperCase(),
        },

        { field: "day_number", headerName: "Day", flex: 0.6 },
        { field: "title", headerName: "Title", flex: 2 },
        { field: "value", headerName: "Description / Value", flex: 3 },

        {
            field: "image",
            headerName: "Image",
            flex: 1.3,
            renderCell: (params) =>
                params.value ? (
                    <img
                        src={`${API_URL}${params.value}`}
                        alt="img"
                        style={{
                            width: "100%",
                            height: "60px",
                            objectFit: "cover",
                            borderRadius: "4px",
                        }}
                    />
                ) : (
                    "—"
                ),
        },

        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            renderCell: (params) => (
                <Box className="flex gap-2">
                    <IconButton color="primary" size="small" onClick={() => handleEditClick(params.row)}>
                        <FiEdit />
                    </IconButton>
                </Box>
            ),
        },
    ];

    const filteredRows =
        filterType === "all"
            ? tableData
            : tableData.filter((row) => row.detail_type === filterType);

    // ===============================
    // RENDER UI
    // ===============================
    return (
        <div className="flex min-h-screen bg-slate-50">
            <ToastContainer position="top-right" autoClose={3000} theme="colored" />

            <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div
                className={`min-h-screen min-w-0 flex-1 transition-[margin] duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"}`}
            >
                <TopBar toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />

                <main className="space-y-6 px-4 pb-8 pt-24 sm:px-6 lg:space-y-10 lg:px-8">
                    <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5">
                        <h2 className="mb-4 text-lg font-semibold text-slate-900 sm:text-xl">Tour Details — Filter View</h2>

                        {/* ADD BUTTON */}
                        <div className="flex justify-end mb-4">
                            <button
                                onClick={() => setOpenAddModal(true)}
                                className="px-4 py-2 bg-green-600 text-white rounded"
                            >
                                + Add New Detail
                            </button>
                        </div>

                        {/* FILTER BUTTONS */}
                        <div className="flex gap-2 mb-6">
                            {["all", "tour", "itinerary", "highlight", "inclusion", "exclusion", "image"].map(
                                (type) => (
                                    <button
                                        key={type}
                                        onClick={() => setFilterType(type)}
                                        className={`px-4 py-2 rounded-lg border capitalize ${
                                            filterType === type ? "bg-black text-white" : "bg-white"
                                        }`}
                                    >
                                        {type}
                                    </button>
                                )
                            )}
                        </div>

                        <DataGrid
                            rows={filteredRows}
                            columns={columns}
                            getRowId={(row) => row.id}
                            pageSize={10}
                            autoHeight
                            loading={loading}
                            disableRowSelectionOnClick
                        />
                    </section>
                </main>
            </div>

            {/* EDIT MODAL */}
            <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} fullWidth maxWidth="sm">
                <DialogTitle>Edit Detail</DialogTitle>

                <DialogContent className="space-y-4 mt-2">
                    <input
                        type="text"
                        name="title"
                        value={editRow?.title || ""}
                        onChange={handleEditInputChange}
                        placeholder="Title"
                        className="w-full p-2 border rounded"
                    />

                    <input
                        type="text"
                        name="value"
                        value={editRow?.value || ""}
                        onChange={handleEditInputChange}
                        placeholder="Value"
                        className="w-full p-2 border rounded"
                    />

                    {editRow?.detail_type === "itinerary" && (
                        <input
                            type="number"
                            name="day_number"
                            value={editRow?.day_number || ""}
                            onChange={handleEditInputChange}
                            placeholder="Day Number"
                            className="w-full p-2 border rounded"
                        />
                    )}

                    <input type="file" accept={IMAGE_UPLOAD_ACCEPT} onChange={handleEditFileChange} />
                    <p className="text-xs text-slate-500">{IMAGE_UPLOAD_HELP}</p>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenEditModal(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleEditSubmit}>
                        Update
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ADD NEW DETAIL MODAL */}
            <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add New Detail</DialogTitle>

                <DialogContent className="space-y-4 mt-2">
                    {/* TYPE */}
                    <select
                        name="detail_type"
                        value={newDetail.detail_type}
                        onChange={(e) =>
                            setNewDetail({ ...newDetail, detail_type: e.target.value })
                        }
                        className="w-full p-2 border rounded"
                    >
                        <option value="">Select Type</option>
                        <option value="itinerary">Itinerary</option>
                        <option value="highlight">Highlight</option>
                        <option value="inclusion">Inclusion</option>
                        <option value="exclusion">Exclusion</option>
                        <option value="image">Image</option>
                    </select>

                    {/* DAY NUMBER */}
                    {newDetail.detail_type === "itinerary" && (
                        <input
                            type="number"
                            placeholder="Day Number"
                            className="w-full p-2 border rounded"
                            onChange={(e) =>
                                setNewDetail({ ...newDetail, day_number: e.target.value })
                            }
                        />
                    )}

                    <input
                        type="text"
                        placeholder="Title"
                        className="w-full p-2 border rounded"
                        onChange={(e) =>
                            setNewDetail({ ...newDetail, title: e.target.value })
                        }
                    />

                    <input
                        type="text"
                        placeholder="Description / Value"
                        className="w-full p-2 border rounded"
                        onChange={(e) =>
                            setNewDetail({ ...newDetail, value: e.target.value })
                        }
                    />

                    {/* IMAGE UPLOAD */}
                    {newDetail.detail_type === "image" && (
                        <>
                            <input
                                type="file"
                                accept={IMAGE_UPLOAD_ACCEPT}
                                onChange={handleNewImageChange}
                            />
                            <p className="text-xs text-slate-500">{IMAGE_UPLOAD_HELP}</p>
                        </>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenAddModal(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddSubmit}>
                        Add Detail
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default ViewTourDetails;
